// Error reporting service for MoZo.Live
import { errorRepository } from '../repositories/error-repository';
import { CreateErrorLogInput, DeviceInfo } from '../../types/error-logs';
import { ErrorSeverity } from '../../types/error-logs';
import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';
// Error reporting now uses internal logging only

// Re-export ErrorSeverity for convenience
export { ErrorSeverity };

// Base error class
export class BaseError extends Error {
  public readonly name: string;
  public readonly severity: ErrorSeverity;
  public readonly context: Record<string, any>;
  public readonly isOperational: boolean;

  constructor(
    message: string,
    name = 'BaseError',
    severity = ErrorSeverity.ERROR,
    isOperational = true,
    context = {}
  ) {
    super(message);
    this.name = name;
    this.severity = severity;
    this.isOperational = isOperational;
    this.context = context;
    
    // Maintains proper stack trace (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

// Get browser and device information
function getBrowserInfo() {
  const { userAgent } = navigator;
  return {
    userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor,
    cookieEnabled: navigator.cookieEnabled
  };
}

function getDeviceInfo(): DeviceInfo {
  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    pixelRatio: window.devicePixelRatio,
    orientation: window.screen.orientation?.type || 'unknown',
    connection: 'connection' in navigator 
      ? {
          effectiveType: (navigator as any).connection?.effectiveType || 'unknown',
          downlink: (navigator as any).connection?.downlink || 0,
          rtt: (navigator as any).connection?.rtt || 0,
          saveData: (navigator as any).connection?.saveData || false
        }
      : 'unknown' as const
  };
}

// Queue for offline error logging
class ErrorQueue {
  private readonly storageKey = 'mozo_error_queue';
  
  // Get queued errors
  getQueue(): Array<CreateErrorLogInput> {
    try {
      const queue = localStorage.getItem(this.storageKey);
      return queue ? JSON.parse(queue) : [];
    } catch (e) {
      console.error('Failed to parse error queue:', e);
      return [];
    }
  }
  
  // Add error to queue
  addToQueue(error: CreateErrorLogInput): void {
    try {
      const queue = this.getQueue();
      queue.push(error);
      localStorage.setItem(this.storageKey, JSON.stringify(queue));
    } catch (e) {
      console.error('Failed to add error to queue:', e);
    }
  }
  
  // Clear queue
  clearQueue(): void {
    localStorage.removeItem(this.storageKey);
  }
  
  // Remove specific error from queue
  removeFromQueue(index: number): void {
    try {
      const queue = this.getQueue();
      queue.splice(index, 1);
      localStorage.setItem(this.storageKey, JSON.stringify(queue));
    } catch (e) {
      console.error('Failed to remove error from queue:', e);
    }
  }
}

// Central error handling service
export class ErrorReportingService {
  private errorQueue = new ErrorQueue();
  private sessionId = uuidv4();
  
  constructor() {
    // Process queued errors when online
    window.addEventListener('online', () => {
      this.processErrorQueue();
    });
  }
  
  // Process queued errors
  async processErrorQueue(): Promise<void> {
    const queue = this.errorQueue.getQueue();
    if (queue.length === 0) return;
    
    for (let i = 0; i < queue.length; i++) {
      try {
        await errorRepository.create(queue[i]);
        this.errorQueue.removeFromQueue(i);
        i--; // Adjust index after removal
      } catch (e) {
        console.error('Failed to process queued error:', e);
      }
    }
  }
  
  // Capture and report an error
  async captureError(
    error: Error | string,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    context: Record<string, any> = {}
  ): Promise<void> {
    // Convert string to error if needed
    const errorObject = typeof error === 'string' ? new Error(error) : error;
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error(errorObject);
    }
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get current route
      const currentRoute = window.location.pathname;
      
      // Get app version (you can set this in your build process)
      const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
      
      // Prepare error data
      const errorData: CreateErrorLogInput = {
        user_id: user?.id || null,
        error_message: errorObject.message,
        error_stack: errorObject.stack || null,
        error_type: errorObject.name || 'Error',
        component: context.component || null,
        url: window.location.href,
        route: currentRoute,
        browser_info: getBrowserInfo(),
        device_info: getDeviceInfo(),
        app_version: appVersion,
        additional_context: {
          ...context,
          sessionId: this.sessionId,
          timestamp: new Date().toISOString()
        }
      };
      
      // Log error to console with severity and context
      console.error(`[${severity}] ${errorObject.message}`, {
        error: errorObject,
        context,
        user: user ? { id: user.id, email: user.email } : null,
        sessionId: this.sessionId
      });
      
      // External error tracking removed
      // Only using internal error logging now
      
      // Check if online
      if (navigator.onLine) {
        // Log to Supabase
        await errorRepository.create(errorData);
      } else {
        // Queue for later if offline
        this.errorQueue.addToQueue(errorData);
      }
      
      // Handle based on severity
      if (severity === ErrorSeverity.FATAL) {
        // For fatal errors, we might want to show a full-screen error
        this.showFatalError(errorObject.message);
      }
    } catch (loggingError) {
      // Fallback if error logging fails
      if (import.meta.env.DEV) {
        console.error('Error while logging error:', loggingError);
      }
      
      // Queue for later if logging fails
      try {
        // Prepare error data
        const errorData: CreateErrorLogInput = {
          user_id: null,
          error_message: errorObject.message,
          error_stack: errorObject.stack || null,
          error_type: errorObject.name || 'Error',
          component: context.component || null,
          url: window.location.href,
          route: window.location.pathname,
          browser_info: getBrowserInfo(),
          device_info: getDeviceInfo(),
          app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
          additional_context: {
            ...context,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            loggingError: loggingError instanceof Error ? loggingError.message : String(loggingError)
          }
        };  
        
        this.errorQueue.addToQueue(errorData);
      } catch (e) {
        // Last resort logging
        console.error('Complete failure in error logging system:', e);
      }
    }
  }
  
  // Show fatal error UI
  showFatalError(message: string): void {
    // External error tracking removed
    // Only using internal error logging now
    
    // Dispatch event for UI to show error
    const event = new CustomEvent('fatal-error', { 
      detail: { message } 
    });
    window.dispatchEvent(event);
  }
  
  // Set up global error handlers
  setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        event.reason || new Error('Unhandled Promise rejection'),
        ErrorSeverity.ERROR,
        { 
          type: 'unhandledrejection',
          handled: false
        }
      );
    });
    
    // Handle uncaught exceptions
    window.addEventListener('error', (event) => {
      // Prevent capturing same error twice
      if (event.error) {
        this.captureError(
          event.error,
          ErrorSeverity.ERROR,
          { 
            type: 'uncaughtexception',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            handled: false
          }
        );
      }
    });
    
    // Set up console error monitoring (optional)
    if (import.meta.env.PROD && import.meta.env.VITE_MONITOR_CONSOLE_ERRORS === 'true') {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        // Call the original console.error
        originalConsoleError.apply(console, args);
        
        // Only capture actual errors, not strings
        const errors = args.filter(arg => arg instanceof Error);
        if (errors.length > 0) {
          this.captureError(errors[0], ErrorSeverity.WARNING, {
            type: 'console.error',
            additionalArgs: args.slice(1).map(arg => 
              typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
            ).join(', ')
          });
        }
      };
    }
    
    // Monitor network status
    window.addEventListener('offline', () => {
      this.captureError(
        'Application went offline',
        ErrorSeverity.WARNING,
        { type: 'connectivity' }
      );
    });
    
    // Handle fatal errors
    window.addEventListener('fatal-error', ((event: CustomEvent) => {
      // Show a full-screen error UI
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; text-align: center; padding: 20px;">
            <h1 style="color: #e11d48; font-size: 24px; margin-bottom: 16px;">Something went wrong</h1>
            <p style="margin-bottom: 24px;">${event.detail?.message || 'The application encountered a critical error.'}</p>
            <button 
              style="background-color: #e11d48; color: white; padding: 12px 24px; border: none; border-radius: 4px; cursor: pointer;"
              onclick="window.location.reload()"
            >
              Reload Application
            </button>
          </div>
        `;
      }
    }) as EventListener);
  }
  
  // Enhanced retry operation with circuit breaker
  async retryWithCircuitBreaker<T>(
    operation: () => Promise<T>,
    options = {
      retries: 3,
      initialDelay: 500,
      maxDelay: 5000,
      factor: 2
    }
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      // Log the error
      this.captureError(
        error instanceof Error ? error : new Error(String(error)),
        ErrorSeverity.ERROR,
        { operation: operation.name || 'anonymous', retryAttempt: 1 }
      );
      
      if (options.retries <= 0) throw error;
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, options.initialDelay));
      
      // Retry with updated options
      return this.retryWithCircuitBreaker(operation, {
        ...options,
        retries: options.retries - 1,
        initialDelay: Math.min(options.initialDelay * options.factor, options.maxDelay)
      });
    }
  }
}

// Export singleton instance
export const errorReportingService = new ErrorReportingService();
